import { useState, useEffect, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";

export default function ImportExcel() {
  const [searchParams] = useSearchParams();
  const initialCompany = searchParams.get("empresa") || "";
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState(initialCompany);
  const [file, setFile] = useState(null);
  const [report, setReport] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef();

  useEffect(() => {
    api.get("/companies").then((r) => {
      setCompanies(r.data);
      if (!companyId && r.data.length) setCompanyId(r.data[0].id);
    });
    // eslint-disable-next-line
  }, []);

  const downloadTemplate = async () => {
    try {
      const r = await api.get("/absenteeism/template", { responseType: "blob" });
      const url = window.URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = "absenteismo-modelo.xlsx";
      document.body.appendChild(a); a.click();
      a.remove(); window.URL.revokeObjectURL(url);
    } catch (err) { toast.error(err.message); }
  };

  const exportCurrent = async () => {
    if (!companyId) {
      toast.error("Selecione uma empresa primeiro.");
      return;
    }
    try {
      const r = await api.get(`/absenteeism/export?company_id=${companyId}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `absenteismo-${companyId.split("-")[0]}.xlsx`;
      document.body.appendChild(a); a.click();
      a.remove(); window.URL.revokeObjectURL(url);
      toast.success("Planilha exportada. Edite e re-importe para atualizar.");
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    }
  };

  const onPick = (f) => {
    if (!f) return;
    if (!f.name.toLowerCase().endsWith(".xlsx")) {
      toast.error("Apenas .xlsx é aceito."); return;
    }
    setFile(f); setReport(null);
  };

  const upload = async () => {
    if (!file || !companyId) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post(`/absenteeism/import?company_id=${companyId}`, fd, {
        headers: { "Content-Type": "multipart/form-data" }, timeout: 120000,
      });
      setReport(r.data);
      const total = r.data.employees_created + r.data.employees_updated + r.data.certificates_created;
      toast.success(`Importação concluída: ${total} registros processados.`);
    } catch (err) { toast.error(err.response?.data?.detail || err.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="abs-import-page">
      <header>
        <div className="overline">Absenteísmo</div>
        <h1 className="font-display text-3xl font-extrabold mt-1">Importação Excel</h1>
        <Link to="/app/absenteismo" className="text-sm text-emerald-800 hover:underline mt-2 inline-block">
          <i className="fa-solid fa-arrow-left mr-2"></i>Voltar ao painel
        </Link>
      </header>

      <div className="card border-2 border-emerald-300 bg-emerald-50/30">
        <div className="flex items-start gap-4">
          <i className="fa-solid fa-file-excel text-4xl text-emerald-700"></i>
          <div className="flex-1">
            <h2 className="font-display text-lg font-extrabold mb-1">Modelo padrão</h2>
            <p className="text-sm text-slate-600 mb-3">
              Baixe o template Excel com 3 abas (Colaboradores, Atestados, Instruções), preencha
              com seus dados e faça o upload aqui. Colaboradores são identificados pela coluna
              <b> Matricula</b> — duplicatas serão atualizadas, não duplicadas.
            </p>
            <div className="flex flex-wrap gap-2">
              <button onClick={downloadTemplate} className="btn btn-primary" data-testid="download-template-btn">
                <i className="fa-solid fa-download"></i> Baixar modelo em branco
              </button>
              <button
                onClick={exportCurrent}
                disabled={!companyId}
                className="btn btn-ghost border border-emerald-300 text-emerald-900 disabled:opacity-50"
                data-testid="export-current-btn"
              >
                <i className="fa-solid fa-file-export"></i> Exportar dados atuais
              </button>
            </div>
            <p className="text-xs text-slate-500 mt-2">
              <i className="fa-solid fa-circle-info mr-1 text-emerald-700"></i>
              Use <b>Exportar dados atuais</b> para baixar a planilha já preenchida com os colaboradores
              e atestados desta empresa. Edite, adicione novas linhas e re-importe — registros existentes
              (mesma matrícula) serão atualizados.
            </p>
          </div>
        </div>
      </div>

      <div className="card">
        <label className="block mb-4">
          <div className="overline mb-1">Empresa de destino</div>
          <select value={companyId} onChange={(e) => setCompanyId(e.target.value)} className="w-full md:w-1/2 border border-slate-300 px-3 py-2">
            {companies.map((c) => <option key={c.id} value={c.id}>{c.razao_social || c.nome_fantasia}</option>)}
          </select>
        </label>
        <div onClick={() => fileRef.current?.click()}
             onDragOver={(e) => e.preventDefault()}
             onDrop={(e) => { e.preventDefault(); onPick(e.dataTransfer.files[0]); }}
             className="border-2 border-dashed border-slate-300 hover:border-emerald-700 hover:bg-emerald-50/40 cursor-pointer p-10 text-center transition-colors"
             data-testid="excel-dropzone">
          <i className="fa-solid fa-cloud-arrow-up text-5xl text-slate-400 mb-3"></i>
          <div className="font-display text-base font-bold">{file ? file.name : "Arraste o .xlsx ou clique para selecionar"}</div>
          <div className="text-xs text-slate-500 mt-1">Máx 10MB</div>
          <input ref={fileRef} type="file" accept=".xlsx" className="hidden"
                 data-testid="excel-file-input"
                 onChange={(e) => onPick(e.target.files[0])} />
        </div>
        {file && (
          <div className="mt-4 flex justify-end">
            <button onClick={upload} disabled={busy} className="btn btn-primary disabled:opacity-50" data-testid="upload-btn">
              {busy ? "Processando…" : <><i className="fa-solid fa-upload mr-2"></i>Importar dados</>}
            </button>
          </div>
        )}
      </div>

      {report && (
        <div className="card" data-testid="import-report">
          <h2 className="font-display text-xl font-extrabold mb-3">Relatório de importação</h2>
          <div className="grid md:grid-cols-3 gap-3 mb-4">
            <div className="border border-emerald-200 bg-emerald-50 p-3">
              <div className="overline">Colaboradores novos</div>
              <div className="font-display text-2xl font-extrabold text-emerald-900">{report.employees_created}</div>
            </div>
            <div className="border border-amber-200 bg-amber-50 p-3">
              <div className="overline">Colaboradores atualizados</div>
              <div className="font-display text-2xl font-extrabold text-amber-900">{report.employees_updated}</div>
            </div>
            <div className="border border-emerald-200 bg-emerald-50 p-3">
              <div className="overline">Atestados criados</div>
              <div className="font-display text-2xl font-extrabold text-emerald-900">{report.certificates_created}</div>
            </div>
          </div>
          {report.errors.length > 0 ? (
            <div className="border border-rose-300 bg-rose-50 p-3">
              <div className="font-bold text-rose-900 mb-2">{report.errors.length} erro(s):</div>
              <ul className="text-sm space-y-1">
                {report.errors.map((e, i) => <li key={i} className="text-rose-800">• {e}</li>)}
              </ul>
            </div>
          ) : (
            <div className="text-sm text-emerald-700"><i className="fa-solid fa-check mr-2"></i>Sem erros.</div>
          )}
        </div>
      )}
    </div>
  );
}
